import './Activities.css';
import React, { createContext } from 'react';
import classnames from 'classnames';
import GlobalProps from '../globalProps';
import { usePaginatedResponse, useData } from '../../model/data';
import { ActivitySubjectType, getActivities, ActivityMetaFilter } from '../../model/activity';
import { Card } from '../Card/Card';
import { Pagination } from '../Pagination/Pagination';
import { t } from '../../locales/translate';
import { ReplyForm } from './ReplyForm';
import { CommentList } from './CommentList';
import { LoadingPage } from '../LoadingPage/LoadingPage';
import { pickBy, compact } from 'lodash';
import { presence } from '../../util/util';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

type MetaFilter = 'shipmentRelevant' | 'productImportant';

interface Props extends GlobalProps {
  subjectId: string;
  subjectType: ActivitySubjectType;
  title?: JSX.Element | string;
  readOnly?: boolean;
  metaFilter?: MetaFilter;
  cacheKey?: number;
  onFlag?: () => void;
  collapseWhenEmpty?: boolean;
  collapseWhenLoading?: boolean;
}

export const ActivitiesContext = createContext({
  reloadActivities: () => {},
});

function determineMetaFilter(metaFilter?: MetaFilter): {} | { meta: ActivityMetaFilter } {
  return pickBy({
    meta: presence(
      pickBy<ActivityMetaFilter>({
        shipmentRelevant: metaFilter === 'shipmentRelevant' || undefined,
        productImportant: metaFilter === 'productImportant' || undefined,
      })
    ),
  });
}

export function Activities({
  subjectId,
  subjectType,
  className,
  style,
  title = t.model.activity._plural,
  readOnly,
  metaFilter,
  cacheKey,
  onFlag,
  collapseWhenEmpty,
  collapseWhenLoading,
}: Props): JSX.Element | null {
  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getActivities({
        page,
        perPage,
        subjectId,
        subjectType,
        ...determineMetaFilter(metaFilter),
      }),
    subjectId + subjectType + cacheKey
  );

  const activities = useData((db) => compact(paginator.currentIds.map((e) => db.table('activities').find(e))));

  const content = paginator.loading ? (
    <LoadingPage title={t.actions.loading(t.model.activity._plural)} />
  ) : paginator.error ? (
    <div>{t.errors.itemNotFound(t.model.activity._plural)}</div>
  ) : (
    <Card
      title={title}
      elevation={2}
      pagination={paginator.totalCount > paginator.perPage ? <Pagination {...paginator} /> : undefined}
      className={classnames('activities', className)}
      style={style}
    >
      {!readOnly && (
        <ReplyForm
          subjectId={subjectId}
          subjectType={subjectType}
          placeholder={t.model.activity.writeComment}
          onSubmit={() => paginator.reloadPage()}
        />
      )}
      <CommentList readOnly={readOnly} comments={activities} onChange={() => paginator.reloadPage()} onFlag={onFlag} />
    </Card>
  );

  const collapsed =
    (collapseWhenLoading && paginator.loading) ||
    (collapseWhenEmpty && !paginator.loading && !paginator.error && activities.length === 0);
  return (
    <div className="activities__wrapper">
      <Collapse open={!collapsed}>{content}</Collapse>
    </div>
  );
}
